/**
 * Settings Page for AgenticRCM
 * Subscription & billing live in one sidebar item (SubscriptionManagement).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../agenticsaas/contexts/AuthContext';
import { useOrganization } from '../agenticsaas/hooks/useOrganization';
import { persistOrganizationProfile } from '@saas-foundation/shared-frontend/utils/organizationContext';
import {
  User,
  Users,
  Shield,
  Bell,
  CreditCard,
  Building2,
  Save,
  X,
  Edit,
  Copy,
  Sparkles,
  LayoutDashboard,
} from 'lucide-react';
import TeamManagement from '../agenticsaas/components/team/TeamManagement';
import SubscriptionManagement from '../agenticsaas/components/subscription/SubscriptionManagement';
import EdiClearinghouseOrganizationSettingSection from '../components/settings/EdiClearinghouseOrganizationSettingSection';
import OrganizationSupportedSpecialtiesSection from '../components/settings/OrganizationSupportedSpecialtiesSection';
import OrganizationDefaultDateRangeSection from '../components/settings/OrganizationDefaultDateRangeSection';
import OrganizationAIContributionSection from '../components/settings/OrganizationAIContributionSection';
import LocationsSection from '../components/settings/LocationsSection';
import ExecutiveCustomTilesSection from '../components/settings/ExecutiveCustomTilesSection';
import ExecutiveDashboardBrandingSection from '../components/settings/ExecutiveDashboardBrandingSection';
import ExecutiveSpreadsheetImportSection from '../components/settings/ExecutiveSpreadsheetImportSection';
import ExecutiveTileLayoutSection from '../components/settings/ExecutiveTileLayoutSection';
import OperationalTileLayoutSection from '../components/settings/OperationalTileLayoutSection';
import SettingsOnboardingPage from './SettingsOnboardingPage';
import {
  DASHBOARD_VIEWS,
  canAccessDashboardView,
  dashboardViewLabel,
  getStoredDashboardPreference,
  isExecutiveRole,
  isFinanceRole,
  resolveDashboardView,
  setStoredDashboardPreference,
} from '../utils/dashboardPreferences';
import { useEffectiveAccess } from '../agenticsaas/contexts/EffectiveAccessContext';
const resolveSettingsTab = (tab) => (tab === 'billing' ? 'subscription' : tab || 'profile');

// Get API base URL for SaaSFoundation
const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const isProduction = window.location.hostname !== 'localhost' || 
                        (window.location.port !== '3000' && window.location.port !== '');
    if (isProduction) {
      return '/api/v1'; // nginx proxies /api to SaaSFoundation:8001
    }
  }
  
  if (typeof window !== 'undefined' && window.location) {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    return (() => { const __h = hostname; return (__h && __h !== 'localhost' && __h !== '127.0.0.1' && !/^(\d{1,3}\.){3}\d{1,3}$/.test(__h)) ? '/api/v1' : `${protocol}//${hostname}:8001/api/v1`; })();
  }
  
  return 'http://localhost:8001/api/v1';
};

const Settings = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateProfile, changePassword, mfaSetup, mfaConfirmSetup } = useAuthContext();
  const { role, applications, permissions, subscription, loading: accessLoading } = useEffectiveAccess();
  const { organization, loading: orgLoading, refreshOrganization, error: orgError, canManageOrganization } = useOrganization();
  const [activeTab, setActiveTab] = useState(() => resolveSettingsTab(searchParams.get('tab')));
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Notification preferences
  const [notifications, setNotifications] = useState({
    email_notifications: true,
    marketing_emails: false,
    security_alerts: true,
    product_updates: true,
    billing_notifications: true
  });

  const [dashboardPreference, setDashboardPreference] = useState(() => getStoredDashboardPreference());

  const [orgForm, setOrgForm] = useState({
    name: '',
    description: '',
    organization_type: 'solo'
  });

  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaEnrollment, setMfaEnrollment] = useState(null);
  const [mfaConfirmCode, setMfaConfirmCode] = useState('');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (!tab) return;
    if (tab === 'billing') {
      setActiveTab('subscription');
      navigate('/settings?tab=subscription', { replace: true });
      return;
    }
    setActiveTab(tab);
  }, [searchParams, navigate]);

  // Update profile data and MFA state when user changes
  useEffect(() => {
    if (user) {
      setProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || ''
      });
      setMfaEnabled(!!user.mfa_enabled);
    }
  }, [user]);

  // Update organization form when organization loads
  useEffect(() => {
    if (organization) {
      setOrgForm({
        name: organization.name || '',
        description: organization.description || '',
        organization_type: organization.organization_type || 'solo'
      });
    }
  }, [organization]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'organization', label: 'Organization', icon: Building2 },
    ...(canManageOrganization ? [{ id: 'onboarding', label: 'Onboarding', icon: Sparkles }] : []),
    { id: 'team', label: 'Team', icon: Users },
    { id: 'subscription', label: 'Subscription & billing', icon: CreditCard },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
  ];

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      await updateProfile(profileData);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Profile update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update profile. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match.' });
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long.' });
      setLoading(false);
      return;
    }
    if (!/[A-Z]/.test(passwordData.newPassword) || !/[a-z]/.test(passwordData.newPassword) || !/\d/.test(passwordData.newPassword)) {
      setMessage({
        type: 'error',
        text: 'Password must include at least one uppercase letter, one lowercase letter, and one number.',
      });
      setLoading(false);
      return;
    }

    try {
      const result = await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setMessage({
        type: 'success',
        text: result?.message || 'Password changed successfully. A confirmation email has been sent.',
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to change password. Please check your current password and try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOrganizationUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await fetch(`${API_BASE_URL}/organizations/current`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(orgForm),
      });

      if (response.ok) {
        const data = await response.json();
        const updated = data.organization || data.data?.organization || data;
        if (updated?.id) {
          persistOrganizationProfile(updated);
        }
        await refreshOrganization();
        setMessage({ type: 'success', text: 'Organization updated successfully!' });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update organization');
      }
    } catch (error) {
      console.error('Organization update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update organization. Please try again.' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDashboardPreferenceChange = (preference) => {
    setDashboardPreference(preference);
    setStoredDashboardPreference(preference);
    const resolved = resolveDashboardView(user, preference, {
      orgRole: role,
      access: { role, applications, permissions, subscription, loading: accessLoading },
    });
    const label =
      preference === 'auto'
        ? `Auto (${dashboardViewLabel(resolved)} for your role)`
        : preference === DASHBOARD_VIEWS.FINANCE
          ? 'Finance View'
          : preference === DASHBOARD_VIEWS.EXECUTIVE
            ? 'Executive KPI Dashboard'
            : 'Operational Dashboard';
    setMessage({ type: 'success', text: `Home dashboard set to ${label}.` });
  };

  const handleNotificationUpdate = async (key, value) => {
    const oldValue = notifications[key];
    setNotifications(prev => ({ ...prev, [key]: value }));

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      
      const response = await fetch(`${API_BASE_URL}/user/preferences`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ [key]: value }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update preferences');
      }
    } catch (error) {
      console.error('Notification update error:', error);
      setNotifications(prev => ({ ...prev, [key]: oldValue }));
      setMessage({ 
        type: 'error', 
        text: 'Failed to update notification preferences. Please try again.' 
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (e) {
      return dateString;
    }
  };

  const getNotificationLabel = (key) => {
    const labels = {
      email_notifications: 'Email Notifications',
      marketing_emails: 'Marketing Emails',
      security_alerts: 'Security Alerts',
      product_updates: 'Product Updates',
      billing_notifications: 'Billing Notifications'
    };
    return labels[key] || key;
  };

  const getNotificationDescription = (key) => {
    const descriptions = {
      email_notifications: 'Receive email notifications for important updates',
      marketing_emails: 'Receive product updates and marketing content',
      security_alerts: 'Get notified about security-related activities',
      product_updates: 'Receive notifications about new features and updates',
      billing_notifications: 'Get notified about billing and subscription changes'
    };
    return descriptions[key] || '';
  };

  const getOrganizationTypeLabel = (type) => {
    const types = {
      solo: 'Solo (1 provider)',
      small_practice: 'Small Practice (2-10 providers)',
      medium_practice: 'Medium Practice (11-50 providers)',
      enterprise: 'Enterprise (>50 providers)'
    };
    return types[type] || type;
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile':
        return (
          <form onSubmit={handleProfileUpdate} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  id="first_name"
                  type="text"
                  value={profileData.first_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  id="last_name"
                  type="text"
                  value={profileData.last_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
            </div>
            <button 
              type="submit" 
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        );

      case 'organization':
        return (
          <div className="space-y-6">
            {orgLoading && (
              <div className="text-center py-4 text-gray-500">Loading organization...</div>
            )}
            {orgError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                Error loading organization: {orgError}
              </div>
            )}
            {!orgLoading && !organization && (
              <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
                No organization data available. Please check console for details.
              </div>
            )}
            {organization && (
              <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-700">Organization ID:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-900 font-mono">
                      {organization.id || organization.organization_id || 'N/A'}
                    </span>
                    {(organization.id || organization.organization_id) && (
                      <button
                        type="button"
                        onClick={() => {
                          const orgId = organization.id || organization.organization_id;
                          navigator.clipboard.writeText(String(orgId));
                          setMessage({ type: 'success', text: 'Organization ID copied to clipboard!' });
                        }}
                        className="p-1 text-gray-500 hover:text-gray-700 transition-colors"
                        title="Copy Organization ID"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Organization Type:</span>
                  <span className="text-sm text-gray-900">
                    {getOrganizationTypeLabel(organization.organization_type)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-700">Created:</span>
                  <span className="text-sm text-gray-900">
                    {formatDate(organization.created_at)}
                  </span>
                </div>
              </div>
            )}
            
            <form onSubmit={handleOrganizationUpdate} className="space-y-6">
              {!canManageOrganization && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
                  Organization details are read-only for your role. Contact an organization owner or admin to make changes.
                </p>
              )}
              <div>
                <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Name
                </label>
                <input
                  id="orgName"
                  type="text"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, name: e.target.value }))}
                  required
                  disabled={loading || !canManageOrganization}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label htmlFor="orgDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="orgDescription"
                  value={orgForm.description}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  disabled={loading || !canManageOrganization}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                />
              </div>
              
              <div>
                <label htmlFor="orgType" className="block text-sm font-medium text-gray-700 mb-1">
                  Organization Type
                </label>
                <select
                  id="orgType"
                  value={orgForm.organization_type}
                  onChange={(e) => setOrgForm(prev => ({ ...prev, organization_type: e.target.value }))}
                  disabled={loading || !canManageOrganization}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="solo">Solo (1 provider)</option>
                  <option value="small_practice">Small Practice (2-10 providers)</option>
                  <option value="medium_practice">Medium Practice (11-50 providers)</option>
                  <option value="enterprise">Enterprise (&gt;50 providers)</option>
                </select>
              </div>
              
              {canManageOrganization && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Updating...' : 'Update Organization'}
              </button>
              )}
            </form>

            <EdiClearinghouseOrganizationSettingSection
              organization={organization}
              onMessage={setMessage}
              onSaved={refreshOrganization}
            />

            <OrganizationSupportedSpecialtiesSection
              organization={organization}
              onMessage={setMessage}
              onSaved={refreshOrganization}
            />
            <OrganizationDefaultDateRangeSection
              organization={organization}
              onMessage={setMessage}
              onSaved={refreshOrganization}
            />
            <OrganizationAIContributionSection
              organization={organization}
              onMessage={setMessage}
              onSaved={refreshOrganization}
            />
            <LocationsSection organization={organization} onMessage={setMessage} />
          </div>
        );

      case 'onboarding':
        return canManageOrganization ? <SettingsOnboardingPage embedded /> : null;

      case 'team':
        return (
          <div>
            <TeamManagement />
          </div>
        );

      case 'subscription':
        return (
          <div>
            <SubscriptionManagement />
          </div>
        );

      case 'security':
        return (
          <div className="space-y-8">
            <form onSubmit={handlePasswordChange} className="space-y-6">
              <h3 className="text-lg font-medium">Change Password</h3>
              <div className="space-y-4 max-w-md">
                <div>
                  <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    id="currentPassword"
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    id="newPassword"
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                    required
                    disabled={loading}
                    placeholder="At least 8 characters"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    required
                    disabled={loading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
              </div>
              <button 
                type="submit" 
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </form>

            {/* Two-Factor Authentication — authenticator app */}
            <div className="mt-10 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-medium mb-2">Two-Factor Authentication (2FA)</h3>
              <p className="text-sm text-gray-600 mb-4">
                Use an authenticator app (Google Authenticator, Authy, 1Password, or Microsoft Authenticator).
                When enabled, sign-in requires the 6-digit code from the app. SMS is not used.
              </p>
              <div className="space-y-4 max-w-md">
                <p className="text-sm">
                  Status:{' '}
                  <span className={`font-medium ${mfaEnabled && !mfaEnrollment ? 'text-green-700' : 'text-gray-700'}`}>
                    {mfaEnabled && !mfaEnrollment ? 'On' : 'Off'}
                  </span>
                </p>
                {mfaEnrollment?.qr_data_url ? (
                  <div className="rounded-md border border-gray-200 p-3 space-y-2">
                    <p className="text-sm text-gray-700">Scan this QR code in your authenticator app, then enter the current 6-digit code.</p>
                    <img src={mfaEnrollment.qr_data_url} alt="Authenticator QR code" className="w-40 h-40" />
                    <p className="text-xs text-gray-500 break-all">Manual key: {mfaEnrollment.secret}</p>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={mfaConfirmCode}
                      onChange={(e) => setMfaConfirmCode(e.target.value.replace(/\D/g, ''))}
                      placeholder="000000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-center tracking-widest"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={mfaSaving || mfaConfirmCode.length < 6}
                        onClick={async () => {
                          setMfaSaving(true);
                          setMessage({ type: '', text: '' });
                          try {
                            await mfaConfirmSetup(mfaConfirmCode);
                            setMfaEnrollment(null);
                            setMfaConfirmCode('');
                            setMfaEnabled(true);
                            setMessage({ type: 'success', text: 'Authenticator 2FA is enabled.' });
                          } catch (err) {
                            setMessage({ type: 'error', text: err.message || 'Invalid code.' });
                          } finally {
                            setMfaSaving(false);
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                      >
                        {mfaSaving ? 'Confirming...' : 'Confirm and enable'}
                      </button>
                      <button
                        type="button"
                        disabled={mfaSaving}
                        onClick={() => {
                          setMfaEnrollment(null);
                          setMfaConfirmCode('');
                          setMfaEnabled(!!user?.mfa_enabled);
                        }}
                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : mfaEnabled ? (
                  <button
                    type="button"
                    disabled={mfaSaving}
                    onClick={async () => {
                      setMfaSaving(true);
                      setMessage({ type: '', text: '' });
                      try {
                        await mfaSetup(false, 'totp');
                        setMfaEnabled(false);
                        setMfaEnrollment(null);
                        setMessage({ type: 'success', text: '2FA is turned off.' });
                      } catch (err) {
                        setMessage({ type: 'error', text: err.message || 'Failed to disable 2FA.' });
                      } finally {
                        setMfaSaving(false);
                      }
                    }}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                  >
                    {mfaSaving ? 'Updating...' : 'Turn off 2FA'}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={mfaSaving}
                    onClick={async () => {
                      setMfaSaving(true);
                      setMessage({ type: '', text: '' });
                      try {
                        const result = await mfaSetup(true, 'totp');
                        if (result?.data?.enrollment || result?.data?.qr_data_url) {
                          setMfaEnrollment(result.data);
                          setMessage({
                            type: 'success',
                            text: result.data.message || 'Scan the QR code, then confirm with a 6-digit code.',
                          });
                        } else {
                          setMessage({ type: 'error', text: 'Could not start authenticator setup. Try again.' });
                        }
                      } catch (err) {
                        setMessage({ type: 'error', text: err.message || 'Failed to start 2FA setup.' });
                      } finally {
                        setMfaSaving(false);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {mfaSaving ? 'Starting...' : 'Enable 2FA'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      case 'dashboard': {
        const accessOpts = {
          orgRole: role,
          access: { role, applications, permissions, subscription, loading: accessLoading },
        };
        const roleDefault = resolveDashboardView(user, 'auto', accessOpts);
        const executiveByRole = isExecutiveRole(user, accessOpts);
        const financeByRole = isFinanceRole(user, accessOpts);
        const orgId = organization?.id || organization?.organization_id;
        const homeOptions = [
          {
            id: 'auto',
            title: 'Automatic (recommended)',
            description: financeByRole && !executiveByRole
              ? 'Your role maps to Finance View.'
              : executiveByRole
                ? 'Your role maps to the Executive KPI Dashboard.'
                : 'Your role maps to the Operational Dashboard.',
            badge: dashboardViewLabel(roleDefault),
          },
          canAccessDashboardView(DASHBOARD_VIEWS.EXECUTIVE, user, accessOpts) && {
            id: DASHBOARD_VIEWS.EXECUTIVE,
            title: 'Executive KPI Dashboard',
            description:
              'Enterprise scorecards, AR aging, payer mix, opportunities, and benchmark comparisons.',
          },
          {
            id: DASHBOARD_VIEWS.OPERATIONAL,
            title: 'Operational Dashboard',
            description:
              'Service health, work queues, denial trends, and frontline RCM metrics.',
          },
          canAccessDashboardView(DASHBOARD_VIEWS.FINANCE, user, accessOpts) && {
            id: DASHBOARD_VIEWS.FINANCE,
            title: 'Finance View',
            description:
              'Platform financial KPIs — NCR, denials, days in A/R, capture, dollars at risk, and aging.',
          },
        ].filter(Boolean);
        return (
          <div className="space-y-10">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Default home dashboard</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Views available to you depend on your role. Executive, Operational, and Finance View
                  stay separate — you only see switches your role is allowed to use.
                </p>
              </div>
              <div className="space-y-3">
                {homeOptions.map((option) => (
                  <label
                    key={option.id}
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      dashboardPreference === option.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="dashboard-preference"
                      value={option.id}
                      checked={dashboardPreference === option.id}
                      onChange={() => handleDashboardPreferenceChange(option.id)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900">{option.title}</span>
                        {option.badge && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            → {option.badge}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="border-t border-gray-200 pt-8">
              <ExecutiveDashboardBrandingSection
                organizationId={orgId ? String(orgId) : ''}
                organizationName={organization?.name}
                disabled={!orgId}
              />
            </div>

            <div className="border-t border-gray-200 pt-8">
              <ExecutiveTileLayoutSection
                organizationId={orgId ? String(orgId) : ''}
                disabled={!orgId}
              />
            </div>

            <div className="border-t border-gray-200 pt-8">
              <OperationalTileLayoutSection
                organizationId={orgId ? String(orgId) : ''}
                disabled={!orgId}
              />
            </div>

            <div className="border-t border-gray-200 pt-8">
              <ExecutiveSpreadsheetImportSection
                organizationId={orgId ? String(orgId) : ''}
                disabled={!orgId}
              />
            </div>

            <div className="border-t border-gray-200 pt-8">
              <ExecutiveCustomTilesSection
                organizationId={orgId ? String(orgId) : ''}
                disabled={!orgId}
              />
            </div>
          </div>
        );
      }

      case 'notifications':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium">Notification Preferences</h3>
            <div className="space-y-4">
              {Object.entries(notifications).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between py-3 border-b border-gray-200">
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      {getNotificationLabel(key)}
                    </label>
                    <p className="text-sm text-gray-500">
                      {getNotificationDescription(key)}
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={value}
                      onChange={(e) => handleNotificationUpdate(key, e.target.checked)}
                      disabled={loading}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              ))}
            </div>
          </div>
        );

      default:
        return null;
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-10xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-600 mt-1">Manage your account settings and preferences</p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg sticky top-4 z-20 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`} role="status">
            {message.text}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-6 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <nav className="space-y-1" role="tablist">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    navigate(`/settings?tab=${tab.id}`, { replace: true });
                  }}
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  className={`w-full text-left px-3 py-2 text-sm font-medium rounded-md transition-colors flex items-center ${
                    activeTab === tab.id
                      ? 'bg-blue-50 text-blue-700 border border-blue-200'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div role="tabpanel">
                {renderTabContent()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;

